import { Api } from '@/api/api.client';
import { uploadFile } from '@/api/resources/file.api';
import { FileUploadType } from '@/api/generated/api.enum';
import type { Components } from '@/api/generated/api';
import { FilterOperationType, type IFilter } from '@chax-at/prisma-filter-common';
import type { IReportFilter } from 'common/interface/filter.interface';
import { ReportStatus } from '@/api/generated/api.enum';

export async function fetchReportsForClass(params: {
  schoolClassId: number;
  $filter: IFilter<IReportFilter>;
  search?: string;
}): Promise<Components.Schemas.PaginatedReportResponseDto> {
  return Api.Routes.Report.getReportsForClass(params);
}

export async function fetchReport(params: { reportId: number }): Promise<Components.Schemas.ReportDto> {
  const result = await Api.Routes.Report.retrieveReport({ reportId: params.reportId });
  return result.data;
}

export async function fetchCompletedReportIdsForGroup(params: {
  schoolClassId: number;
  groupId: number;
}): Promise<number[]> {
  const reportIds: number[] = [];
  let count: number;
  do {
    const page = await fetchReportsForClass({
      schoolClassId: params.schoolClassId,
      $filter: {
        limit: 100,
        offset: reportIds.length,
        filter: [
          { field: 'groupId', type: FilterOperationType.Eq, value: params.groupId },
          { field: 'status', type: FilterOperationType.Eq, value: ReportStatus.completed },
        ],
        order: [{ field: 'id', dir: 'asc' }],
      },
    });
    reportIds.push(...page.rows.map((report) => report.id));
    count = page.count;
    if (!page.rows.length) break;
  } while (reportIds.length < count);
  return reportIds;
}

export async function createReportsForClass(params: {
  schoolClassId: number;
  dto: Components.Schemas.CreateBulkReportsDto;
}): Promise<Components.Schemas.BaseReportDto[]> {
  const result = await Api.Routes.Report.createBulkForClass({ schoolClassId: params.schoolClassId }, params.dto);
  return result.data;
}

export async function createReportsForClassAsync(params: {
  schoolClassId: number;
  dto: Components.Schemas.CreateBulkReportsDto;
}): Promise<Components.Schemas.AsyncOperationCreatedResponseDto> {
  return Api.Routes.Report.createBulkForClassAsync({ schoolClassId: params.schoolClassId }, params.dto);
}

export async function updateReport(params: {
  reportId: number;
  dto: Components.Schemas.UpdateReportDto;
}): Promise<void> {
  await Api.Routes.Report.updateReport({ reportId: params.reportId }, params.dto);
}

export async function updateReportSubject(params: {
  reportId: number;
  reportSubjectId: number;
  dto: Components.Schemas.UpdateReportSubjectDto;
}): Promise<void> {
  await Api.Routes.Report.updateReportSubject(
    { reportId: params.reportId, subjectId: params.reportSubjectId },
    params.dto,
  );
}

export async function updateReportComment(params: {
  reportId: number;
  dto: Components.Schemas.UpdateReportGeneralCommentDto;
}): Promise<void> {
  await Api.Routes.Report.updateReportComment({ reportId: params.reportId }, params.dto);
}

export async function updateReportCompetence(params: {
  reportId: number;
  competenceId: number;
  schoolClassId: number;
  dto: Components.Schemas.UpdateReportCompetenceDto;
}): Promise<void> {
  const { dto, ...rest } = params;
  await Api.Routes.Report.updateReportCompetence(rest, dto);
}

export async function uploadReportCoverImage(params: { reportId: number; file: File }): Promise<void> {
  const uploaded = await uploadFile(FileUploadType.reportCoverImage, params.file);
  await Api.Routes.Report.setReportCoverImage({ reportId: params.reportId }, { fileId: uploaded.id });
}

export async function removeReportCoverImage(params: { reportId: number }): Promise<void> {
  await Api.Routes.Report.removeReportCoverImage({ reportId: params.reportId });
}

export async function closeReport(params: { reportId: number }): Promise<void> {
  await Api.Routes.Report.closeReport({ reportId: params.reportId });
}

export async function deleteReport(params: { reportId: number }): Promise<void> {
  await Api.Routes.Report.deleteReport({ reportId: params.reportId });
}

export async function deleteReportSubject(params: { reportId: number; reportSubjectId: number }): Promise<void> {
  await Api.Routes.Report.deleteReportSubject({ reportId: params.reportId, subjectId: params.reportSubjectId });
}

export async function fetchObservedSubjectIds(params: Components.Schemas.GetObservedSubjectIdsDto): Promise<number[]> {
  const result = await Api.Routes.Report.getObservedSubjectIds(null, params);
  return result.data;
}

export async function fetchNoteSubjectIds(
  params: Components.Schemas.GetObservedSubjectIdsDto,
): Promise<Components.Schemas.NoteSubjectIdsDto> {
  const result = await Api.Routes.Report.getNoteSubjectIds(null, params);
  return result.data;
}

export async function fetchCanReportBeCreated(params: {
  schoolClassId: number;
}): Promise<Components.Schemas.EReportDataStatus | null> {
  const result = await Api.Routes.Report.canReportBeCreated({ schoolClassId: params.schoolClassId });
  return result.data;
}

export async function fetchReportCreationInterval(params: {
  schoolClassId: number;
}): Promise<Components.Schemas.ReportCreationIntervalDto> {
  const result = await Api.Routes.Report.getReportCreationInterval({ schoolClassId: params.schoolClassId });
  return result.data;
}

export async function fetchReportForPrint(params: { reportId: number }): Promise<Components.Schemas.ReportDto> {
  const result = await Api.Routes.Report.getReportForPrint({ reportId: params.reportId });
  return result.data;
}

export async function bulkPrintReports(
  dto: Components.Schemas.BulkPdfGenerationDto,
): Promise<Components.Schemas.AsyncOperationCreatedResponseDto> {
  return Api.Routes.Report.bulkPrintReports({}, dto);
}
